import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faChevronDown,
    faChevronUp,
    faClock,
    faHourglassHalf,
    faBolt,
    faCheckCircle,
    faLink,
    faTag,
    faCog,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';

const formatDateTime = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const calcularDiferencia = (fechaInicioStr, fechaFinStr) => {
    if (!fechaInicioStr || !fechaFinStr) return null;
    const inicio = new Date(fechaInicioStr);
    const fin = new Date(fechaFinStr);
    if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return null;
    const diffMs = fin.getTime() - inicio.getTime();
    if (diffMs < 0) return null;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const remMins = diffMins % 60;
    if (hours < 24) {
        return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

const extraerTicketCombinado = item => {
    if (item.idTicketDestino) return item.idTicketDestino;
    if (item.idTicketPrincipal) return item.idTicketPrincipal;
    if (item.idTicketCombinado) return item.idTicketCombinado;
    return null;
};

const HistorialTickets = ({ idClienteAkasia }) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token } = useToken();
    const [tickets, setTickets] = useState([]);
    const [cargando, setCargando] = useState(false);

    // Estados para despliegue de movimientos / eventos del ticket
    const [expandedTicketId, setExpandedTicketId] = useState(null);
    const [ticketEventsMap, setTicketEventsMap] = useState({});
    const [isLoadingEventsMap, setIsLoadingEventsMap] = useState({});
    const [ticketEtiquetasMap, setTicketEtiquetasMap] = useState({});

    // Cargar etiquetas/incidencias asignadas al ticket
    const fetchTicketEtiquetas = async idTicket => {
        if (!idTicket || !token) return;
        try {
            const response = await fetch(`/api/SupportEngine/GetTicketEtiquetas?idTicket=${idTicket}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                setTicketEtiquetasMap(prev => ({ ...prev, [idTicket]: Array.isArray(list) ? list : [] }));
            }
        } catch {
            // Silencioso
        }
    };

    useEffect(() => {
        const fetchHistorialTickets = async () => {
            if (!idClienteAkasia) {
                setTickets([]);
                return;
            }

            setCargando(true);
            try {
                const response = await fetch(`/api/cliente/ObtenerHistorialTickets?idClienteAkasia=${idClienteAkasia}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1) {
                        const parsed = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                        const lista = parsed.table2 || parsed.Table1 || [];
                        const validList = Array.isArray(lista) ? lista : [];
                        setTickets(validList);
                        validList.forEach(t => {
                            if (t.idTicket) fetchTicketEtiquetas(t.idTicket);
                        });
                    } else {
                        setTickets([]);
                    }
                } else {
                    setTickets([]);
                }
            } catch (error) {
                console.error('Error al obtener historial de tickets:', error);
                setTickets([]);
            } finally {
                setCargando(false);
            }
        };

        fetchHistorialTickets();
    }, [idClienteAkasia, token]);

    // Cargar eventos del ticket desde la API
    const fetchTicketEvents = async idTicket => {
        if (!idTicket || !token) return;
        setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: true }));
        try {
            const response = await fetch(`/api/SupportEngine/GetTicketEvents?idTicket=${idTicket}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                setTicketEventsMap(prev => ({ ...prev, [idTicket]: Array.isArray(list) ? list : [] }));
            } else {
                setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
            }
        } catch (err) {
            console.error('Error al cargar eventos del ticket:', err);
            setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
        } finally {
            setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: false }));
        }
    };

    const handleToggleTicketDetails = idTicket => {
        if (expandedTicketId === idTicket) {
            setExpandedTicketId(null);
            return;
        }

        setExpandedTicketId(idTicket);
        if (!ticketEventsMap[idTicket]) {
            fetchTicketEvents(idTicket);
        }
        if (!ticketEtiquetasMap[idTicket]) {
            fetchTicketEtiquetas(idTicket);
        }
    };

    const getNivelSoporteTexto = nivel => {
        switch (nivel) {
            case 1:
                return 'Nivel 1 – Básico';
            case 2:
                return 'Nivel 2 – Intermedio';
            case 3:
                return 'Nivel 3 – Avanzado';
            case 4:
                return 'Nivel 4 – Especialista';
            case 5:
                return 'Nivel 5 – Cotización';
            default:
                return nivel ? `Nivel ${nivel}` : 'Nivel 1 – Básico';
        }
    };

    const getEstadoConfig = (iEstadoTicket, vEstadoDesc) => {
        const estadoNum = Number(iEstadoTicket);
        switch (estadoNum) {
            case 1:
                return {
                    texto: vEstadoDesc || 'Abierto',
                    borderColor: '#2563eb',
                    nodeColor: '#3b82f6',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#eff6ff',
                        color: isDark ? '#60a5fa' : '#1d4ed8',
                        border: isDark ? '1px solid rgba(37, 99, 235, 0.4)' : '1px solid #bfdbfe'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : '#dbeafe',
                        color: isDark ? '#60a5fa' : '#1e40af',
                        border: isDark ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid #bfdbfe'
                    }
                };
            case 3:
                return {
                    texto: vEstadoDesc || 'En progreso',
                    borderColor: '#d97706',
                    nodeColor: '#f59e0b',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#fffbeb',
                        color: isDark ? '#fbbf24' : '#b45309',
                        border: isDark ? '1px solid rgba(217, 119, 6, 0.4)' : '1px solid #fde68a'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7',
                        color: isDark ? '#fbbf24' : '#8c531b',
                        border: isDark ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid #d8b965'
                    }
                };
            case 4:
                return {
                    texto: vEstadoDesc || 'Combinado',
                    borderColor: '#a855f7',
                    nodeColor: '#a855f7',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                        color: isDark ? '#c084fc' : '#7e22ce',
                        border: isDark ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid #d8b4fe'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(168, 85, 247, 0.18)' : '#f3e8ff',
                        color: isDark ? '#c084fc' : '#7e22ce',
                        border: isDark ? '1px solid rgba(168, 85, 247, 0.35)' : '1px solid #d8b4fe'
                    }
                };
            case 2:
            default:
                return {
                    texto: vEstadoDesc || 'Resuelto',
                    borderColor: '#059669',
                    nodeColor: '#10b981',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#ecfdf5',
                        color: isDark ? '#34d399' : '#047857',
                        border: isDark ? '1px solid rgba(5, 150, 105, 0.4)' : '1px solid #a7f3d0'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.18)' : '#d1fae5',
                        color: isDark ? '#34d399' : '#065f46',
                        border: isDark ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid #a7f3d0'
                    }
                };
        }
    };

    return (
        <Card
            className="shadow-sm border-0 h-100"
            style={{
                borderRadius: '12px',
                backgroundColor: isDark ? '#0b1727' : '#ffffff'
            }}
        >
            <Card.Header
                className="border-bottom py-3 d-flex justify-content-between align-items-center"
                style={{
                    backgroundColor: isDark ? '#0e1d30' : '#f9fafd',
                    borderColor: isDark ? '#233142' : '#edf2f9',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                }}
            >
                <h5 className="mb-0 fw-bold" style={{ color: isDark ? '#f9fafd' : '#0b1727' }}>
                    Historial de Tickets
                </h5>
                {cargando ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                ) : (
                    <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            fontSize: '0.8rem'
                        }}
                    >
                        {tickets.length} {tickets.length === 1 ? 'Ticket' : 'Tickets'}
                    </span>
                )}
            </Card.Header>

            <Card.Body className="p-3">
                {cargando ? (
                    <div className="text-center py-4 text-muted">
                        <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                        <small>Cargando historial de tickets...</small>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <small>No hay tickets registrados para este cliente.</small>
                    </div>
                ) : (
                    <div className="position-relative ps-4 pe-2 py-2">
    

                        {tickets.map((item, idx) => {
                            const isExpanded = expandedTicketId === item.idTicket;
                            const events = ticketEventsMap[item.idTicket] || [];
                            const isLoadingEvents = isLoadingEventsMap[item.idTicket];

                            // Combinación de tickets
                            const isCombinado = Boolean(item.bIsCombinado) || Number(item.iEstadoTicket) === 4 || item.vEstadoDesc?.toLowerCase() === 'combinado';
                            const ticketDestino = isCombinado ? extraerTicketCombinado(item) : null;
                            const estado = getEstadoConfig(isCombinado ? 4 : item.iEstadoTicket, isCombinado ? 'Combinado' : item.vEstadoDesc);

                            // Cálculos de tiempos
                            const tiempoEspera = item.dFechaAsignacion
                                ? calcularDiferencia(item.dCreatedDate, item.dFechaAsignacion)
                                : item.dFechaCierre
                                    ? calcularDiferencia(item.dCreatedDate, item.dFechaCierre)
                                    : calcularDiferencia(item.dCreatedDate, new Date());

                            const tiempoResolucion = item.dFechaCierre
                                ? calcularDiferencia(item.dFechaAsignacion, item.dFechaCierre)
                                : null;

                            const fechaHoraCierre = item.dFechaCierre ? formatDateTime(item.dFechaCierre) : null;

                            // Incidencias / Etiquetas
                            const incidenciasList = ticketEtiquetasMap[item.idTicket] || (item.vEtiquetas ? item.vEtiquetas.split(',').map(e => ({ vNombre: e.trim() })) : item.vIncidencia ? [{ vNombre: item.vIncidencia }] : []);

                            return (
                                <div key={`ticket-timeline-${item.idTicket || idx}`} className="position-relative mb-4">
                                    {/* Tarjeta del ticket con borde izquierdo dinámico según estado */}
                                    <div
                                        className="p-3 border shadow-sm transition-all position-relative"
                                        style={{
                                            borderRadius: '12px',
                                            borderLeft: `5px solid ${estado.borderColor}`,
                                            backgroundColor: isDark ? '#121e2d' : '#ffffff',
                                            borderColor: isDark ? '#233142' : '#e2e8f0',
                                            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.25)' : '0 1px 3px rgba(0, 0, 0, 0.04)',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div
                                            className="cursor-pointer"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggleTicketDetails(item.idTicket)}
                                            title="Clic para ver/ocultar movimientos del ticket"
                                        >
                                            {/* Fila 1: Ticket # (Badge Dinámico) y Fecha a la izq | Estado y Chevron a la der */}
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="badge rounded-pill px-2 py-1 fw-bold"
                                                        style={estado.ticketBadgeStyle}
                                                    >
                                                        Ticket #{item.idTicket}
                                                    </span>
                                                    <small style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.8rem' }} className="d-flex align-items-center gap-1">
                                                        <FontAwesomeIcon icon={faClock} className="text-muted" style={{ fontSize: '0.75rem' }} />
                                                        {formatDateTime(item.dCreatedDate)}
                                                    </small>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="px-2 py-1 fw-semibold"
                                                        style={{
                                                            fontSize: '0.75rem',
                                                            borderRadius: '6px',
                                                            ...estado.badgeStyle
                                                        }}
                                                    >
                                                        {estado.texto}
                                                    </span>
                                                    <FontAwesomeIcon
                                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                                        style={{ color: isDark ? '#94a3b8' : '#9ca3af', fontSize: '0.75rem' }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Fila 2: Título / Asunto */}
                                            <h5 className="fw-bold mb-1 fs-1 lh-sm" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                                {item.vAsunto}
                                            </h5>

                                            {/* Fila 3: Métricas del Ticket */}
                                            <div className="d-flex flex-wrap gap-2 align-items-center mb-2 pt-2">
                                                {/* Tiempo de espera */}
                                                {tiempoEspera && (
                                                    <span
                                                        className="px-2 py-1 fw-medium d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
                                                            color: isDark ? '#cbd5e1' : '#475569',
                                                            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                                                        }}
                                                        title="Tiempo en espera antes de ser atendido"
                                                    >
                                                        <FontAwesomeIcon icon={faHourglassHalf} className="text-muted" style={{ fontSize: '0.7rem' }} />
                                                        <span>Espera</span>
                                                        <strong style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{tiempoEspera}</strong>
                                                    </span>
                                                )}

                                                {/* Tiempo de resolución */}
                                                {tiempoResolucion && (
                                                    <span
                                                        className="px-2 py-1 fw-medium d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5',
                                                            color: isDark ? '#34d399' : '#047857',
                                                            border: isDark ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid #a7f3d0'
                                                        }}
                                                        title="Tiempo total que tomó resolver este ticket"
                                                    >
                                                        <FontAwesomeIcon icon={faBolt} className="text-success" style={{ fontSize: '0.7rem' }} />
                                                        <span>Resolución</span>
                                                        <strong style={{ color: isDark ? '#f9fafd' : '#065f46' }}>{tiempoResolucion}</strong>
                                                    </span>
                                                )}

                                                {/* Fecha y hora de cierre */}
                                                {fechaHoraCierre && (
                                                    <span
                                                        className="px-2 py-1 fw-medium d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
                                                            color: isDark ? '#f87171' : '#b91c1c',
                                                            border: isDark ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid #fecaca'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCheckCircle} className="text-danger" style={{ fontSize: '0.7rem' }} />
                                                        <span>Cerrado</span>
                                                        <strong style={{ color: isDark ? '#f9fafd' : '#991b1b' }}>{fechaHoraCierre}</strong>
                                                    </span>
                                                )}

                                                {/* Si el ticket se combinó */}
                                                {isCombinado && (
                                                    <span
                                                        className="px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? 'rgba(168, 85, 247, 0.18)' : '#f3e8ff',
                                                            color: isDark ? '#c084fc' : '#7e22ce',
                                                            border: isDark ? '1px solid rgba(168, 85, 247, 0.35)' : '1px solid #d8b4fe'
                                                        }}
                                                        title="Este ticket fue combinado"
                                                    >
                                                        <FontAwesomeIcon icon={faLink} style={{ fontSize: '0.7rem' }} />
                                                        <span>{ticketDestino ? `Combinado con Ticket #${ticketDestino}` : 'Ticket Combinado'}</span>
                                                    </span>
                                                )}

                                                {/* Incidencia(s) */}
                                                {incidenciasList.length > 0 && incidenciasList.map((inc, i) => (
                                                    <span
                                                        key={`inc-${i}`}
                                                        className="px-2 py-1 fw-medium d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                                            color: isDark ? '#60a5fa' : '#1d4ed8',
                                                            border: isDark ? '1px solid rgba(59, 130, 246, 0.25)' : '1px solid #bfdbfe'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTag} className="text-primary" style={{ fontSize: '0.7rem' }} />
                                                        <span>Incidencia:</span>
                                                        <strong style={{ color: isDark ? '#f9fafd' : '#1e40af' }}>{inc.vNombre || inc.vNombreEtiqueta || inc}</strong>
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Fila 4: Nivel de soporte y Creado por */}
                                            <div
                                                className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 pt-2 border-top"
                                                style={{
                                                    borderColor: isDark ? '#233142' : '#f1f5f9',
                                                    fontSize: '0.78rem',
                                                    color: isDark ? '#94a3b8' : '#64748b'
                                                }}
                                            >
                                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                                    <span
                                                        className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                        style={{
                                                            backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                                            color: isDark ? '#cbd5e1' : '#475569',
                                                            border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                                                            fontSize: '0.72rem'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCog} className="text-muted" style={{ fontSize: '0.7rem' }} />
                                                        <span>{getNivelSoporteTexto(item.idCatNivelSoporte)}</span>
                                                    </span>
                                                    {item.vCreatedUser && (
                                                        <span
                                                            className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                            style={{
                                                                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                                                color: isDark ? '#cbd5e1' : '#475569',
                                                                border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                                                                fontSize: '0.72rem'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faUser} className="text-muted" style={{ fontSize: '0.7rem' }} />
                                                            <strong style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{item.vCreatedUser}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Panel desplegable con movimientos del ticket */}
                                        {isExpanded && (
                                            <div
                                                className="mt-3 p-3 border"
                                                style={{
                                                    borderRadius: '10px',
                                                    backgroundColor: isDark ? '#0e1d30' : '#f8fafc',
                                                    borderColor: isDark ? '#233142' : '#e2e8f0',
                                                    animation: 'fadeIn 0.2s ease-in'
                                                }}
                                            >
                                                {/* Fecha de cierre si existe */}
                                                {item.dFechaCierre && Number(item.iEstadoTicket) !== 1 && (
                                                    <div
                                                        className="d-flex justify-content-between pb-2 mb-2 border-bottom"
                                                        style={{
                                                            fontSize: '0.78rem',
                                                            borderColor: isDark ? '#233142' : '#e2e8f0',
                                                            color: isDark ? '#94a3b8' : '#64748b'
                                                        }}
                                                    >
                                                        <span className="fw-medium">Cerrado:</span>
                                                        <span style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{formatDateTime(item.dFechaCierre)}</span>
                                                    </div>
                                                )}

                                                {/* Título de movimientos */}
                                                <div
                                                    className="fw-semibold mb-2 d-flex align-items-center justify-content-between"
                                                    style={{
                                                        fontSize: '0.74rem',
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                        color: isDark ? '#94a3b8' : '#64748b'
                                                    }}
                                                >
                                                    <span>MOVIMIENTOS DEL TICKET</span>
                                                    {isLoadingEvents && <FontAwesomeIcon icon={faSpinner} spin className="text-primary" />}
                                                </div>

                                                {isLoadingEvents ? (
                                                    <div className="text-center py-3" style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" />
                                                        Cargando movimientos...
                                                    </div>
                                                ) : events.length === 0 ? (
                                                    <div className="text-center py-2 fst-italic" style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                        Sin movimientos registrados.
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="position-relative ps-3 my-2"
                                                        style={{ borderLeft: isDark ? '2px solid #233142' : '2px solid #cbd5e1' }}
                                                    >
                                                        {[...events]
                                                            .sort((a, b) => (b.idTicketHistorial || 0) - (a.idTicketHistorial || 0))
                                                            .map(ev => {
                                                                const isCierreEv = ev.vTipoEvento === 'CIERRE';
                                                                const isCreacionEv = ev.vTipoEvento === 'CREACION';
                                                                const isReaperturaEv = ev.vTipoEvento === 'REAPERTURA';
                                                                const isCombinadoEv = ev.vTipoEvento === 'COMBINACION_TICKETS';

                                                                const dotColor = isCierreEv
                                                                    ? '#ef4444'
                                                                    : isReaperturaEv
                                                                        ? '#f59e0b'
                                                                        : isCreacionEv
                                                                            ? '#10b981'
                                                                            : isCombinadoEv
                                                                                ? '#a855f7'
                                                                                : '#3b82f6';

                                                                return (
                                                                    <div key={ev.idTicketHistorial} className="mb-3 ps-2 position-relative">
                                                                        <div
                                                                            className="position-absolute rounded-circle"
                                                                            style={{
                                                                                left: '-20px',
                                                                                top: '5px',
                                                                                width: '8px',
                                                                                height: '8px',
                                                                                backgroundColor: dotColor
                                                                            }}
                                                                        />
                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                            <span className="fw-semibold" style={{ fontSize: '0.8rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                                                                {ev.vTitulo}
                                                                            </span>
                                                                            <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                                                {formatDateTime(ev.dCreatedDate)}
                                                                            </span>
                                                                        </div>
                                                                        {ev.vDescripcion && (
                                                                            <div className="mt-1" style={{ fontSize: '0.76rem', color: isDark ? '#cbd5e1' : '#475569' }}>
                                                                                {ev.vDescripcion}
                                                                            </div>
                                                                        )}
                                                                        {ev.vCreatedUser && (
                                                                            <div className="mt-1" style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                                                Por: <span className="fw-medium" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{ev.vCreatedUser}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

HistorialTickets.propTypes = {
    idClienteAkasia: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HistorialTickets;